import { createSelector } from 'reselect'
import { AssetsInitialStateType } from '../reducers/assets'

type AssetsStateType = { assets: AssetsInitialStateType }

// PLOP_APPEND_PATTERN_ANCHOR

// CREATE MULTIPLE ASSETS
export const assetsCreateLoadingSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssets.isLoading,
  isLoading => isLoading
)

export const assetsWasCreatedSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssets.wasCreated,
  wasCreated => wasCreated
)

export const assetsCreatedDataSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssets.data,
  data => data
)

export const assetsCreateErrorSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssets.error,
  error => error
)

// GET ASSETS
export const assetsIsLoadingSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assets.isLoading,
  isLoading => isLoading
)

export const assetsWasLoadedSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assets.wasLoaded,
  wasLoaded => wasLoaded
)

export const assetsSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assets.data.results,
  results => results
)

export const imageAssetsSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assets.data.results,
  results => {
    return results
      .filter(asset => asset.image_asset)
      .map(imageAsset => ({
        id: imageAsset.id,
        resource_name: imageAsset.resource_name,
        name: imageAsset.name,
        url: imageAsset.image_asset.full_size.url,
        width: imageAsset.image_asset.full_size.width_pixels,
        height: imageAsset.image_asset.full_size.height_pixels
      }))
  }
)

export const assetsNextPageTokenSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assets.data.next_page_token,
  next_page_token => next_page_token
)

export const assetsErrorSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assets.error,
  error => error
)

// CREATE ASSET GROUP
export const assetGroupCreateDataSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssetGroup.data,
  data => data
)

export const assetGroupCreateLoadingSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssetGroup.isLoading,
  isLoading => isLoading
)

export const assetGroupWasCreatedSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssetGroup.wasCreated,
  wasCreated => wasCreated
)

export const assetGroupCreateErrorSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createdAssetGroup.error,
  error => error
)

// GET ASSET GROUPS
export const assetGroupsSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroups.results,
  // filter only real asset groups, remove totals item
  results => results.filter(item => item.id)
)

export const assetGroupsIsLoadingSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroups.isLoading,
  isLoading => isLoading
)

export const assetGroupsWasLoadedSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroups.wasLoaded,
  wasLoaded => wasLoaded
)

export const assetGroupsErrorSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroups.error,
  error => error
)

// GET ASSET GROUP
export const assetGroupSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroup.data,
  data => data
)

export const assetGroupIsLoadingSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroup.isLoading,
  isLoading => isLoading
)

export const assetGroupWasLoadedSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroup.wasLoaded,
  wasLoaded => wasLoaded
)

export const assetGroupErrorSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.assetGroup.error,
  error => error
)

// UPDATE ASSET GROUP
export const updateAssetGroupSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.updatedAssetGroup.data,
  data => data
)

export const updateAssetGroupIdSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.updatedAssetGroup.id,
  id => id
)

export const updateAssetGroupIsLoadingSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.updatedAssetGroup.isLoading,
  isLoading => isLoading
)

export const updateAssetGroupWasUpdatedSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.updatedAssetGroup.wasUpdated,
  wasUpdated => wasUpdated
)

export const updateAssetGroupErrorSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.updatedAssetGroup.error,
  error => error
)

// CREATE ASSET GROUP SIGNAL
export const createAssetGroupSignalIsLoadingSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createAssetGroupSignal.isLoading,
  isLoading => isLoading
)

export const createAssetGroupSignalWasCreatedSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createAssetGroupSignal.wasCreated,
  wasCreated => wasCreated
)

export const createAssetGroupSignalErrorSelector = createSelector(
  ({ assets }: AssetsStateType) => assets.createAssetGroupSignal.error,
  error => error
)

// approve internal asset group
export const approveInternalAssetGroupIsLoadingSelector = (id: string | number) =>
  createSelector(
    ({ assets }: AssetsStateType) => assets.approveInternalAssetGroup[id]?.isLoading,
    isLoading => isLoading
  )

export const approveInternalAssetGroupErrorSelector = (id: string | number) =>
  createSelector(
    ({ assets }: AssetsStateType) => assets.approveInternalAssetGroup[id]?.error,
    error => error
  )

export const approveInternalAssetGroupWasApprovedSelector = (id: string | number) =>
  createSelector(
    ({ assets }: AssetsStateType) => assets.approveInternalAssetGroup[id]?.wasApproved,
    wasApproved => wasApproved
  )

// disapprove asset group
export const disapproveInternalAssetGroupIsLoadingSelector = (id: string | number) =>
  createSelector(
    ({ assets }: AssetsStateType) => assets.disapproveInternalAssetGroup[id]?.isLoading,
    isLoading => isLoading
  )

export const disapproveInternalAssetGroupErrorSelector = (id: string | number) =>
  createSelector(
    ({ assets }: AssetsStateType) => assets.disapproveInternalAssetGroup[id]?.error,
    error => error
  )

export const disapproveInternalAssetGroupWasDisapprovedSelector = (id: string | number) => {
  return createSelector(
    ({ assets }: AssetsStateType) => assets.disapproveInternalAssetGroup[id]?.wasDisapproved,
    wasDisapproved => wasDisapproved
  )
}
